import React from "react";
import { View, Text, Image, StyleSheet, Pressable, Linking } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import {
  Reveal,
  WebClass,
  CountUp,
  FONT_DISPLAY,
  FONT_MONO,
} from "./_shared";

// =============================================================================
// Voxcraft — About page sections
//
// Single file containing every section of the About page so the page lays
// itself out top-to-bottom in one place. Each section is a small subcomponent
// using the same Reveal + WebClass + theme-token pattern as the home page so
// the design language stays consistent.
// =============================================================================

// ---------------------------------------------------------------------------
// 1. Hero — eyebrow, bold + soft title pair, lede, animated stat strip.
// ---------------------------------------------------------------------------
const STATS = [
  { to: 2018,    suffix: "",    decimals: 0, label: "Year founded" },
  { to: 12,      suffix: "k+",  decimals: 0, label: "Extensions deployed" },
  { to: 3,       suffix: "",    decimals: 0, label: "Industries served" },
  { to: 99.99,   suffix: "%",   decimals: 2, label: "Uptime SLA" },
];

function AboutHero() {
  const { theme } = useTheme();
  const isMobile = useMobile();
  return (
    <View style={[styles.section, { paddingTop: isMobile ? 80 : 130, paddingBottom: isMobile ? 32 : 64 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>ABOUT VOXCRAFT</Text>
            <Text style={[styles.bigTitle, { color: theme.text }]}>Connecting the unconnected.</Text>
            <Text style={[styles.bigTitleSoft, { color: theme.textDim }]}>
              From small business to the open ocean.
            </Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              Voxcraft is a multi-tenant business PBX built by NT Connect Holdings, Inc. We started in
              maritime, where every dropped packet is a real problem, and grew into the platform that
              now ships into small businesses, naval fleets, and federal government deployments.
            </Text>
          </View>
        </Reveal>

        <Reveal>
          <View style={[styles.statStrip, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}>
            {STATS.map((s, i) => (
              <View
                key={i}
                style={[
                  styles.statCell,
                  i < STATS.length - 1 && !isMobile && { borderRightColor: theme.borderSoft, borderRightWidth: 1 },
                  isMobile && i > 0 && { borderTopColor: theme.borderSoft, borderTopWidth: 1 },
                ]}
              >
                <View style={styles.statValueRow}>
                  <CountUp to={s.to} decimals={s.decimals} style={[styles.statValue, { color: theme.text }]} />
                  {!!s.suffix && <Text style={[styles.statUnit, { color: theme.brand2 }]}>{s.suffix}</Text>}
                </View>
                <Text style={[styles.statLabel, { color: theme.textDim }]}>{s.label}</Text>
              </View>
            ))}
          </View>
        </Reveal>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// 2. Story — two-column narrative with a quoted callout on the right.
// ---------------------------------------------------------------------------
function AboutStory() {
  const { theme } = useTheme();
  const isMobile = useMobile();
  return (
    <View style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={[styles.container, styles.twoCol, isMobile && styles.twoColMobile]}>
        <Reveal style={[styles.colLeft, isMobile && styles.colMobile]}>
          <Text style={[styles.eyebrow, { color: theme.textMute }]}>OUR STORY</Text>
          <Text style={[styles.title, { color: theme.text }]}>Where we started{"\n"}and where we're going.</Text>
          <Text style={[styles.body, { color: theme.textDim }]}>
            NT Connect was founded in <Text style={{ color: theme.text, fontWeight: "700" }}>2018</Text> to
            serve government, enterprise, and maritime clients with reliable communications
            infrastructure. We earned our scars in the toughest environment on Earth: at sea, where
            every routing decision happens behind a satellite that's busy dropping the call.
          </Text>
          <Text style={[styles.body, { color: theme.textDim, marginTop: 14 }]}>
            That same platform now provisions PBXs for small businesses in afternoons, hardens
            communications for naval strike groups in 90 days, and runs inside Avaya's FedRAMP-authorized
            Government Cloud. Voxcraft is what we call it today: one platform for every industry that
            still needs a real phone system.
          </Text>
        </Reveal>

        <Reveal delay={1} style={[styles.colRight, isMobile && styles.colMobile]}>
          <WebClass
            className="voxcraft-ind-card"
            style={[styles.quoteCard, { backgroundColor: theme.bg2, borderColor: theme.borderSoft }]}
          >
            <Text style={[styles.quoteMark, { color: theme.brand2 }]}>“</Text>
            <Text style={[styles.quoteText, { color: theme.text }]}>
              Partnering with Avaya's Gov Cloud is a transformative step in our mission to deliver
              secure, efficient, and scalable communication solutions to government entities.
            </Text>
            <View style={[styles.quoteByline, { borderTopColor: theme.borderSoft }]}>
              <Text style={[styles.quoteName, { color: theme.text }]}>Garry Paxinos</Text>
              <Text style={[styles.quoteRole, { color: theme.textMute }]}>CTO & Chief AI Officer · NT Connect</Text>
            </View>
          </WebClass>
        </Reveal>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// 3. Mission & Vision — paired block on the bg2 surface.
// ---------------------------------------------------------------------------
function AboutMission() {
  const { theme } = useTheme();
  const isMobile = useMobile();
  return (
    <View
      style={[
        styles.section,
        styles.bandedSection,
        { backgroundColor: theme.bg2, borderColor: theme.borderSoft },
        { paddingVertical: isMobile ? 64 : 110 },
      ]}
    >
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>MISSION & VISION</Text>
            <Text style={[styles.title, { color: theme.text }]}>Why we're here.</Text>
          </View>
        </Reveal>

        <View style={[styles.mvGrid, isMobile && styles.mvGridMobile]}>
          <Reveal style={[styles.mvCell, isMobile && styles.mvCellMobile]}>
            <WebClass
              className="voxcraft-feat-card"
              style={[styles.mvCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
            >
              <Text style={[styles.mvLabel, { color: theme.brand2 }]}>MISSION</Text>
              <Text style={[styles.mvBody, { color: theme.text }]}>
                To empower operators with reliable, secure communications infrastructure, so that
                every line, every tenant, and every conversation just works, no matter where it
                originates.
              </Text>
            </WebClass>
          </Reveal>

          <Reveal delay={1} style={[styles.mvCell, isMobile && styles.mvCellMobile]}>
            <WebClass
              className="voxcraft-feat-card"
              style={[styles.mvCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
            >
              <Text style={[styles.mvLabel, { color: theme.green }]}>VISION</Text>
              <Text style={[styles.mvBody, { color: theme.text }]}>
                To redefine business communications by providing resilient, next-generation
                infrastructure that transforms how organizations connect, from cubicle to bridge to
                operations center.
              </Text>
            </WebClass>
          </Reveal>
        </View>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// 4. Values — 4 cards.
// ---------------------------------------------------------------------------
const VALUES = [
  {
    key: "innovation",
    label: "01",
    name: "Innovation",
    desc: "We push past comfortable, well-understood phone-system patterns. The platform's AI setup, multi-tenant scale, and dialplan tooling are all bets that paid off.",
  },
  {
    key: "reliability",
    label: "02",
    name: "Reliability",
    desc: "99.99% uptime SLA. Recordings retained per policy. Dialplans that degrade gracefully when satellite drops. Boring infrastructure is a feature.",
  },
  {
    key: "security",
    label: "03",
    name: "Security",
    desc: "Per-user JWT with rotating refresh, RBAC per resource, multi-tenant isolation, FedRAMP-aligned controls. We ship into the Navy and into your office on the same code path.",
  },
  {
    key: "partnership",
    label: "04",
    name: "Partnership",
    desc: "From concept to support, we stay close. Every customer in our impact studies still has a direct line to the team that delivered for them.",
  },
];

function AboutValues() {
  const { theme } = useTheme();
  const isMobile = useMobile();
  return (
    <View style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>HOW WE WORK</Text>
            <Text style={[styles.title, { color: theme.text }]}>Four values.{"\n"}Every release.</Text>
          </View>
        </Reveal>

        <View style={[styles.valuesGrid, isMobile && styles.valuesGridMobile]}>
          {VALUES.map((v, i) => (
            <View key={v.key} style={[styles.valueCell, isMobile && styles.valueCellMobile]}>
              <Reveal delay={(i % 4) + 1} style={styles.revealFill}>
                <WebClass
                  className="voxcraft-feat-card"
                  style={[styles.valueCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
                >
                  <Text style={[styles.valueNum, { color: theme.brand2 }]}>{v.label}</Text>
                  <Text style={[styles.valueName, { color: theme.text }]}>{v.name}</Text>
                  <Text style={[styles.valueDesc, { color: theme.textDim }]}>{v.desc}</Text>
                </WebClass>
              </Reveal>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// 5. Sister brands — NOOZ.ai and NT Connect, with their own logos.
// ---------------------------------------------------------------------------
function ExternalLinkArrow({ color }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
      <Path stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M9 7h8v8" />
    </Svg>
  );
}

function AboutBrands() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  const open = (url) => Linking.openURL(url);

  return (
    <View
      style={[
        styles.section,
        styles.bandedSection,
        { backgroundColor: theme.bg2, borderColor: theme.borderSoft },
        { paddingVertical: isMobile ? 64 : 110 },
      ]}
    >
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>ALSO FROM NT CONNECT</Text>
            <Text style={[styles.title, { color: theme.text }]}>Sister brands.</Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              NT Connect Holdings is the parent. Beyond Voxcraft, we also build and operate two product
              lines you may know. Open links to learn more about each.
            </Text>
          </View>
        </Reveal>

        <View style={[styles.brandsGrid, isMobile && styles.brandsGridMobile]}>
          <View style={[styles.brandCell, isMobile && styles.brandCellMobile]}>
            <Reveal style={styles.revealFill}>
              <WebClass
                className="voxcraft-ind-card"
                style={[styles.brandCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
              >
                <Pressable onPress={() => open("https://nooz.ai/app")} style={styles.brandInner}>
                  <View style={[styles.brandLogoTile, { backgroundColor: theme.bg2, borderColor: theme.borderSoft, borderWidth: 1 }]}>
                    <Image
                      source={require("../../../assets/images/partners/logo-nooz.png")}
                      style={styles.brandLogoImg}
                      resizeMode="contain"
                    />
                  </View>
                  <Text style={[styles.brandName, { color: theme.text }]}>NOOZ.ai</Text>
                  <Text style={[styles.brandDesc, { color: theme.textDim }]}>
                    AI-powered news bias detection. Free iOS and Android app that reads any article and tells
                    you what the framing is doing.
                  </Text>
                  <View style={[styles.brandLink, { borderTopColor: theme.borderSoft }]}>
                    <Text style={[styles.brandLinkText, { color: theme.brand2, marginRight: 6 }]}>nooz.ai</Text>
                    <ExternalLinkArrow color={theme.brand2} />
                  </View>
                </Pressable>
              </WebClass>
            </Reveal>
          </View>

          <View style={[styles.brandCell, isMobile && styles.brandCellMobile]}>
            <Reveal delay={1} style={styles.revealFill}>
              <WebClass
                className="voxcraft-ind-card"
                style={[styles.brandCard, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}
              >
                <Pressable onPress={() => open("https://ntconnect.com")} style={styles.brandInner}>
                  <View style={[styles.brandLogoTile, { backgroundColor: theme.bg2, borderColor: theme.borderSoft, borderWidth: 1 }]}>
                    <Image
                      source={require("../../../assets/images/nt-connect-white.png")}
                      style={styles.brandLogoImg}
                      resizeMode="contain"
                    />
                  </View>
                  <Text style={[styles.brandName, { color: theme.text }]}>NT Connect</Text>
                  <Text style={[styles.brandDesc, { color: theme.textDim }]}>
                    The parent company. Government, enterprise, and DevOps services including NT E911,
                    NT DevOps, and the broader communications portfolio.
                  </Text>
                  <View style={[styles.brandLink, { borderTopColor: theme.borderSoft }]}>
                    <Text style={[styles.brandLinkText, { color: theme.brand2, marginRight: 6 }]}>ntconnect.com</Text>
                    <ExternalLinkArrow color={theme.brand2} />
                  </View>
                </Pressable>
              </WebClass>
            </Reveal>
          </View>
        </View>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Export — one component renders the whole About page top-to-bottom.
// ---------------------------------------------------------------------------
export default function NtVoxcraftAbout() {
  return (
    <>
      <AboutHero />
      <AboutStory />
      <AboutMission />
      <AboutValues />
      <AboutBrands />
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  bandedSection: { borderTopWidth: 1, borderBottomWidth: 1 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },

  head: { maxWidth: 760, marginBottom: 40 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  bigTitle:     { fontFamily: FONT_DISPLAY, fontSize: 56, fontWeight: "800", letterSpacing: -1.6, lineHeight: 58 },
  bigTitleSoft: { fontFamily: FONT_DISPLAY, fontSize: 56, fontWeight: "500", letterSpacing: -1.6, lineHeight: 58, marginBottom: 18 },
  title:        { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  lede:         { fontSize: 17, lineHeight: 27, maxWidth: 720 },
  body:         { fontSize: 15, lineHeight: 25, maxWidth: 540 },

  // Hero stat strip — same shape as the marketing Features stats strip.
  statStrip: { flexDirection: "row", flexWrap: "wrap", borderRadius: 12, borderWidth: 1, overflow: "hidden", marginTop: 32 },
  statCell: { flex: 1, padding: 24, minWidth: 160 },
  statValueRow: { flexDirection: "row", alignItems: "baseline" },
  statValue: { fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: "800", letterSpacing: -1, lineHeight: 36 },
  statUnit: { fontSize: 18, fontWeight: "700", marginLeft: 4 },
  statLabel: { fontSize: 12, marginTop: 8 },

  // Story — 2 columns with a quote card on the right.
  twoCol: { flexDirection: "row", alignItems: "stretch", gap: 56 },
  twoColMobile: { flexDirection: "column", gap: 32 },
  colLeft: { flex: 1.1, justifyContent: "center" },
  colRight: { flex: 1, justifyContent: "center" },
  colMobile: { width: "100%", flex: undefined },
  quoteCard: { borderRadius: 16, borderWidth: 1, padding: 32 },
  quoteMark: { fontFamily: FONT_DISPLAY, fontSize: 72, fontWeight: "800", lineHeight: 54, marginBottom: 4 },
  quoteText: { fontSize: 17, lineHeight: 27, marginBottom: 22 },
  quoteByline: { borderTopWidth: 1, paddingTop: 16 },
  quoteName: { fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: "700" },
  quoteRole: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.5, marginTop: 4, textTransform: "uppercase" },

  // Mission / Vision pair
  mvGrid: { flexDirection: "row", alignItems: "stretch", marginHorizontal: -9 },
  mvGridMobile: { flexDirection: "column", marginHorizontal: 0 },
  mvCell: { flex: 1, padding: 9 },
  mvCellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  mvCard: { flex: 1, borderRadius: 14, borderWidth: 1, padding: 32 },
  mvLabel: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 14, textTransform: "uppercase" },
  mvBody: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: "600", letterSpacing: -0.4, lineHeight: 30 },

  // Values grid
  valuesGrid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -9 },
  valuesGridMobile: { flexDirection: "column", marginHorizontal: 0 },
  valueCell: { width: "25%", padding: 9 },
  valueCellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  revealFill: { flex: 1, width: "100%" },
  valueCard: { flex: 1, borderRadius: 14, borderWidth: 1, padding: 26 },
  valueNum: { fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: "800", letterSpacing: 1.6, marginBottom: 12 },
  valueName: { fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: "700", letterSpacing: -0.2, marginBottom: 10 },
  valueDesc: { fontSize: 13.5, lineHeight: 21 },

  // Sister brands
  brandsGrid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -9 },
  brandsGridMobile: { flexDirection: "column", marginHorizontal: 0 },
  brandCell: { width: "50%", padding: 9 },
  brandCellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  brandCard: { flex: 1, borderRadius: 14, borderWidth: 1, overflow: "hidden" },
  brandInner: { flex: 1, padding: 28 },
  brandLogoTile: {
    height: 72,
    paddingHorizontal: 18, paddingVertical: 12,
    borderRadius: 12,
    alignItems: "center", justifyContent: "center",
    marginBottom: 22, alignSelf: "flex-start",
    minWidth: 160,
  },
  brandLogoImg: { width: 140, height: 48 },
  brandName: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: "800", letterSpacing: -0.4, marginBottom: 10 },
  brandDesc: { fontSize: 14, lineHeight: 22, marginBottom: 22, flex: 1 },
  brandLink: { borderTopWidth: 1, paddingTop: 14, flexDirection: "row", alignItems: "center" },
  brandLinkText: { fontFamily: FONT_MONO, fontSize: 12, fontWeight: "700", letterSpacing: 0.4 },
});
